/**
 * Reload the page when a new service worker takes control (7HAI-1253).
 *
 * THE PROBLEM THIS SOLVES. The service worker is generated with `skipWaiting`,
 * `clientsClaim` and `cleanupOutdatedCaches`. On every deploy, for a tab that is
 * already open, that combination means:
 *
 *   1. the new worker activates at once instead of waiting for the tab to close;
 *   2. it takes control of a page whose HTML and entry bundle are build N;
 *   3. it deletes build N's precache.
 *
 * The page is now running code whose chunks are gone from the cache — and the
 * deploy removed them from the server too. Its next lazy import fails, which for
 * most people is the moment they log in. That is the "every update breaks the
 * first login" report.
 *
 * The missing piece was never the caching. It is that NOTHING REPLACED THE
 * DOCUMENT at the moment of the swap. Seizing a page without reloading it leaves
 * it running code the worker underneath it has already disowned.
 *
 * WHY THIS AND NOT `lazyWithRetry` (7HAI-1224). That module reloads after an
 * import has already failed — recovery, and a backstop worth keeping for a chunk
 * that 404s with no controller change. This one acts at the swap itself, before
 * anything has had a chance to break. Prevention beats recovery here because the
 * failure it prevents is a crash the user sees.
 */

/** True once a reload has been started, so a burst of events cannot stack. */
let reloading = false;

/** sessionStorage key counting swap-reloads in this tab. */
export const SW_RELOAD_COUNT = "bid:sw-reload-count";

/**
 * How many swap-reloads one tab may perform before giving up.
 *
 * WHY A CAP AT ALL. A reload triggered by `controllerchange` normally happens
 * once per deploy, and the module flag above is enough — the reload replaces the
 * document and nothing fires again until the next deploy. But during a ROLLING
 * deploy, two instances serve different `sw.js` files, and a browser can be
 * handed the old worker and the new one alternately. Each swap would trigger a
 * reload, each reload a fresh swap: an infinite loop, which is strictly worse
 * than the crash this module exists to prevent.
 *
 * Three allows several legitimate deploys during one long session while bounding
 * the pathological case. Past the cap the page simply stays put, and
 * lazyWithRetry remains as the recovery path.
 */
export const SW_RELOAD_MAX = 3;

/** sessionStorage access that cannot itself throw — private-mode Safari and some
 *  embedded webviews raise on access rather than returning null. A guard that
 *  crashes while guarding is worse than no guard, so a failure reads as "already
 *  at the cap" and simply declines to reload. */
function reloadCount(): number {
  try {
    return Number(window.sessionStorage.getItem(SW_RELOAD_COUNT) ?? "0") || 0;
  } catch {
    return SW_RELOAD_MAX; // cannot count => must not start
  }
}

function bumpReloadCount(next: number): void {
  try {
    window.sessionStorage.setItem(SW_RELOAD_COUNT, String(next));
  } catch {
    /* the read guard above already declines in this case */
  }
}

/** Injected in tests; `window.location.reload` in the browser. */
export type Reloader = () => void;

/**
 * Start watching for a service-worker swap. Returns a teardown function.
 *
 * Safe to call when service workers are unavailable (older browsers, insecure
 * origins, some embedded webviews): it does nothing and returns a no-op.
 */
export function installSwUpdateReload(
  reload: Reloader = () => window.location.reload(),
): () => void {
  const noop = () => {};
  if (typeof navigator === "undefined") return noop;

  // Truthiness, not `in`: the property can be PRESENT and undefined (some
  // embedded webviews, and any environment that stubs navigator), and an `in`
  // check would wave that through and then throw on the next line.
  const container = navigator.serviceWorker;
  if (!container) return noop;

  // THE LOAD-BEARING CHECK. `controllerchange` also fires on a first-ever visit,
  // when the controller goes from null to the newly installed worker. There is
  // no previous build to replace then, so reloading would refresh the page on
  // every new visitor for no reason. Only a page that ALREADY had a controller
  // can have had one swapped out from under it.
  const hadController = container.controller != null;
  if (!hadController) return noop;

  const onControllerChange = () => {
    // Once only. The reload replaces the document, but the event can fire more
    // than once in a race, and a second reload would interrupt the first.
    if (reloading) return;

    // And not endlessly across reloads: see SW_RELOAD_MAX. The module flag above
    // dies with the document, so the cap has to live in sessionStorage to be
    // able to see the previous reload at all.
    const seen = reloadCount();
    if (seen >= SW_RELOAD_MAX) return;

    reloading = true;
    bumpReloadCount(seen + 1);
    reload();
  };

  container.addEventListener("controllerchange", onControllerChange);
  return () => container.removeEventListener("controllerchange", onControllerChange);
}

/** Test seam: forget that a reload was started. Not used in the browser, where
 *  the reload discards this module's state anyway. */
export function __resetSwUpdateReloadForTests(): void {
  reloading = false;
  try {
    window.sessionStorage.removeItem(SW_RELOAD_COUNT);
  } catch {
    /* ignore */
  }
}
